import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import Surface from '../components/ui/Surface';
import IconWatermark from '../components/ui/IconWatermark';
import TrustStrip from '../components/TrustStrip';
import ProcessTimeline from '../components/ProcessTimeline';
import LeadMagnetForm from '../components/LeadMagnetForm';
import IntakeForm from '../components/IntakeForm';
import StructuredData from '../components/StructuredData';
import SubscribeForm from '../components/SubscribeForm';
import HomeHero from '../components/HomeHero';
import {
  GraduationCap,
  Users,
  Briefcase
} from '../components/icons';
import { StethoscopeClassic, USFlagFaint } from '../components/icons/ClassicBrandIcons';

const segments = [
  { label: 'Emerging Professionals', id: 'emerging-professionals', icon: GraduationCap, wmSize: { w: 88, h: 88 } },
  { label: 'Busy Families', id: 'busy-families', icon: Users, wmSize: { w: 92, h: 92 } },
  { label: 'Business Owners', id: 'business-owners', icon: Briefcase, wmSize: { w: 92, h: 92 } },
  { label: 'Medical Professionals', id: 'medical-pros', icon: StethoscopeClassic, wmSize: { w: 96, h: 96 } },
  { label: 'Active Duty & Veterans', id: 'active-duty-vets', icon: USFlagFaint, wmSize: { w: 130, h: 70 } },
  { label: 'Near-Retirees / Retirees', id: 'near-retirees', icon: Users, wmSize: { w: 92, h: 92 } }
];

const services = [
  { label: 'Financial Planning', id: 'financial-planning' },
  { label: 'Tax Planning & Preparation', id: 'tax-planning' },
  { label: 'Investment Management', id: 'investment-management' },
  { label: 'Advanced Wealth Strategies', id: 'advanced-wealth' },
  { label: 'Generational Wealth Management', id: 'generational-wealth' },
  { label: 'Custom High Net Worth Solutions', id: 'custom-hnw' }
];

const insightTopics = [
  { label: 'Retirement Planning', id: 'retirement-planning' },
  { label: 'Tax Strategies', id: 'tax-strategies' },
  { label: 'Estate Planning', id: 'estate-planning' },
  { label: 'College Planning', id: 'college-planning' },
  { label: 'Business Exit', id: 'business-exit' },
  { label: 'Charitable Giving', id: 'charitable-giving' },
  { label: 'Risk Management', id: 'risk-management' },
  { label: 'Market Updates', id: 'market-updates' },
  { label: 'Behavioral Finance', id: 'behavioral-finance' }
];

export default function Home() {
  return (
    <main>
      <Helmet>
        <title>Carter Asset Management | Secured Freedom, for Every Stage of Your Wealth</title>
      </Helmet>
      <StructuredData />
      <HomeHero />
      <TrustStrip />
      <WhoWeServe />
      <Services />
      <ProcessTimeline />
      <InsightsTopics />
      <NoCostConsultation />
      <LeadMagnet />
      <NewsletterSection />
    </main>
  );
}

function WhoWeServe() {
  return (
    <section className="bg-white py-10 md:py-24" id="who-we-serve">
      <div className="section-container">
        <div className="max-w-3xl">
          <span className="text-sm font-semibold uppercase tracking-wide text-cam-blue/70">Who We Serve</span>
          <h2 className="mt-3 text-xl md:text-3xl font-semibold tracking-tight text-slate-900">
            Personalized planning for households navigating meaningful decisions.
          </h2>
          <p className="mt-2 text-slate-600">
            Every plan begins with listening—understanding where money intersects with your values, responsibilities, and the impact you want to create.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {segments.map((segment) => {
            const Icon = segment.icon;
            return (
              <Link
                key={segment.id}
                to={`/who-we-serve#${segment.id}`}
                aria-label={`Learn more for ${segment.label}`}
                className="group block rounded-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cam-blue"
              >
                <Surface className="relative overflow-hidden p-6 md:p-8 shadow-sm hover:shadow-glow hover:ring-cam-blue/30 hover:-translate-y-0.5 will-change-transform transition">
                  <h3 className="text-base md:text-lg font-semibold text-slate-900 transition group-hover:text-cam-blue">
                    {segment.label}
                  </h3>

                  {Icon ? (
                    <IconWatermark size={segment.wmSize} className="text-cam-blue/20">
                      <Icon className="h-full w-full" aria-hidden="true" preserveAspectRatio="xMidYMid meet" />
                    </IconWatermark>
                  ) : null}
                </Surface>
              </Link>
            );
          })}
        </div>
        <div className="mt-10 flex justify-center">
          <Link to="/who-we-serve" className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cam-blue rounded-lg w-full sm:w-auto">
            <Button variant="primary" className="w-full sm:w-auto">Learn More</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="bg-slate-50 py-10 md:py-24" id="services">
      <div className="section-container">
        <h2 className="text-xl md:text-3xl font-semibold tracking-tight text-slate-900">
          Integrated services aligned to what matters.
        </h2>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {services.map((service) => (
            <Link
              key={service.id}
              to={`/services#${service.id}`}
              aria-label={`Learn more about ${service.label}`}
              className="group block rounded-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cam-blue"
            >
              <Surface className="p-6 md:p-8 shadow-sm hover:shadow-glow hover:ring-cam-blue/30 hover:-translate-y-0.5 transition will-change-transform">
                <h3 className="text-base md:text-lg font-semibold text-slate-900 transition group-hover:text-cam-blue">
                  {service.label}
                </h3>
              </Surface>
            </Link>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Button variant="primary" href="/services" className="w-full sm:w-auto">
            See the details
          </Button>
        </div>
      </div>
    </section>
  );
}

function InsightsTopics() {
  return (
    <section className="bg-slate-50 py-10 md:py-24" id="insights">
      <div className="section-container">
        <div className="max-w-3xl">
          <span className="text-sm font-semibold uppercase tracking-wide text-cam-blue/70">Insights</span>
          <h2 className="mt-3 text-xl md:text-3xl font-semibold tracking-tight text-slate-900">
            Clarity you can apply between our meetings.
          </h2>
          <p className="mt-2 text-slate-600">Choose a topic you’re interested in.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {insightTopics.map((topic) => (
            <a
              key={topic.id}
              href={`/insights#${topic.id}`}
              aria-label={`Read ${topic.label} insights`}
              className="group block rounded-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cam-blue"
            >
              <Surface className="p-6 md:p-8 shadow-sm hover:shadow-glow hover:ring-cam-blue/30 hover:-translate-y-0.5 transition">
                <h3 className="text-base md:text-lg font-semibold text-slate-900 group-hover:text-cam-blue">{topic.label}</h3>
              </Surface>
            </a>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <Button variant="primary" href="/insights" className="w-full sm:w-auto">
            See all topics
          </Button>
        </div>
      </div>
    </section>
  );
}

function LeadMagnet() {
  return (
    <section className="bg-slate-50 py-10 md:py-24">
      <div className="section-container grid gap-8 md:gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <div>
          <h2 className="mt-3 text-xl md:text-3xl font-semibold tracking-tight text-slate-900">Download “The 5-Step Check-Up”.</h2>
          <p className="mt-2 text-slate-600">
            Get a concise guide to evaluate risk, cash flow, taxes, and investment alignment. Replace this PDF with your compliance-approved resource before launch.
          </p>
        </div>
        <Surface className="p-6 md:p-8">
          <LeadMagnetForm />
          <p className="mt-3 text-sm text-slate-500">We only email resources relevant to your request and you can unsubscribe anytime.</p>
        </Surface>
      </div>
    </section>
  );
}

function NoCostConsultation() {
  return (
    <section className="bg-white py-10 md:py-24" id="no-cost-consultation">
      <div className="section-container">
        <div className="grid gap-8 md:gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-start">
          <div>
            <span className="text-sm font-semibold uppercase tracking-wide text-cam-blue/70">No-Cost Consultation</span>
            <h2 className="mt-3 text-xl md:text-3xl font-semibold tracking-tight text-slate-900">
              A no-pressure way to get started.
            </h2>
            <p className="mt-2 text-slate-600">
              Share context on your goals, timeline, and comfort with risk. We will review and reach out to see how we can be of service.
            </p>
          </div>
          <Surface className="p-6 md:p-8">
            <IntakeForm />
          </Surface>
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  return (
    <section className="bg-white py-10 md:py-24">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <Surface className="p-6 md:p-8">
          <div className="md:flex md:items-center md:justify-between md:gap-8">
            <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">Stay current with new insights.</h2>
            <p className="mt-2 text-slate-600 md:mt-0 md:max-w-sm">
              Receive objective, educational market updates and investing insights crafted for you.
            </p>
          </div>
          <SubscribeForm className="mt-6" />
        </Surface>
      </div>
    </section>
  );
}
